import type { LegacyRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { PAGE_POSITIONS } from 'types';
import type { AffiliateStockListing, CapiProductPage } from 'types/capi/pdp';
import { fromProductDetailPage } from 'types/TrackableProduct';

import DiscountCode from '~/components/DiscountCode';
import ProductNotificationButton from '~/components/ProductNotificationButton';
import StockStatusPill from '~/components/StockStatus';
import type { StockStatus as StockStatusType } from '~/domain/stockStatus';
import usePageData from '~/hooks/usePageData';
import useRegion from '~/hooks/useRegion';
import useRetailerInteraction from '~/hooks/useRetailerInteraction';
import AffiliateLogo from '~/routes/pdps/components/WhereToBuy/AffiliateLogo';
import { formatReleaseDateLong } from '~/utils/dates';
import { getStockStatusForSize } from '~/utils/listings';

import { BuyNow, VisitSite } from '../../components/Buttons';
import AffiliateSizes from './AffiliateSizes';
import ShippingAndPrice from './ShippingAndPrice';

type Props = {
  listing: AffiliateStockListing;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
};

export default function StockListing({
  listing,
  position,
  selectedSize,
  pagePosition
}: Props) {
  const product = usePageData<CapiProductPage>();
  const fireRetailerInteraction = useRetailerInteraction();

  const stockStatus = getStockStatusForSize(listing, selectedSize);

  const onRetailerClick = (size?: string) =>
    fireRetailerInteraction({
      listing,
      placement: 'list',
      selectedSize: size ?? selectedSize
    });

  return (
    <div className="flex flex-col gap-4 rounded-lg bg-white p-4">
      <div className="flex gap-4 bg-white">
        <div className="flex grow items-center gap-4">
          <div className="h-9 w-16">
            <AffiliateLogo
              src={listing.affiliate.logo}
              alt={listing.affiliate.name}
            />
          </div>
          <div>
            <StockStatus
              stockStatus={stockStatus}
              listing={listing}
              selectedSize={selectedSize}
            />
          </div>
        </div>
        <ShippingAndPrice listing={listing} selectedSize={selectedSize} />
      </div>
      <AffiliateSizes
        product={fromProductDetailPage(product)}
        price={listing.price}
        affiliate={listing.affiliate}
        selectedSize={selectedSize}
        sizes={listing.sizes}
        urlTemplates={listing}
        position={position}
        pagePosition={pagePosition}
        onSizeClick={onRetailerClick}
      />
      <div className="flex w-full flex-col gap-2">
        <DiscountCode
          code={listing.discount_code?.code}
          amount={listing.discount_code?.amount}
        />
        <StockListingButtons
          stockStatus={stockStatus}
          selectedSize={selectedSize}
          listing={listing}
          position={position}
          pagePosition={pagePosition}
          onRetailerClick={onRetailerClick}
        />
      </div>
    </div>
  );
}

export const StockStatus = ({
  stockStatus,
  listing,
  selectedSize
}: {
  listing: AffiliateStockListing;
  selectedSize?: string;
  stockStatus: StockStatusType;
}) => {
  const { t } = useTranslation();
  const region = useRegion();

  const isReseller = listing.affiliate.is_reseller;

  switch (stockStatus) {
    case 'in-stock':
      return (
        <div className="flex flex-col items-start gap-1">
          <StockStatusPill status={isReseller ? 'reseller' : stockStatus} />
          {isReseller && listing.affiliate.id === 'affiliate:marketplace' && (
            <p className="text-xs text-black md:text-sm">Verified sellers</p>
          )}
        </div>
      );
    case 'coming-soon':
      return (
        <div className="flex flex-col items-start gap-1">
          <StockStatusPill status={stockStatus} />
          <span className="text-sm md:text-base">
            {listing.release_date
              ? formatReleaseDateLong(listing.release_date, region)
              : t('tbcLabel')}
          </span>
        </div>
      );
    case 'sold-out':
      return (
        <div className="flex flex-col items-start gap-1">
          <StockStatusPill status={stockStatus} />
          {Boolean(listing.sizes.length) && selectedSize && (
            <p className="text-sm text-black md:text-base">UK {selectedSize}</p>
          )}
        </div>
      );
    case 'tbc':
    default:
      return <StockStatusPill status="tbc" />;
  }
};

type StockListingButtonsProps = {
  buttonsRef?: LegacyRef<HTMLDivElement>;
  dynamicVariant?: boolean;
  listing: AffiliateStockListing;
  onBuyNowClick?: () => void;
  onRetailerClick?: (size?: string) => void;
  pagePosition: PAGE_POSITIONS;
  position: number;
  selectedSize?: string;
  showAfffiliateLogo?: boolean;
  stockStatus: StockStatusType;
};

export function StockListingButtons({
  stockStatus,
  pagePosition,
  selectedSize,
  listing,
  position,
  onBuyNowClick,
  onRetailerClick,
  showAfffiliateLogo,
  dynamicVariant
}: StockListingButtonsProps) {
  const product = usePageData<CapiProductPage>();
  const region = useRegion();

  const areNotificationsAllowed =
    product.allow_user_notifications && region.code === 'GB';

  const onBuyNow =
    onBuyNowClick || onRetailerClick
      ? () => {
          onBuyNowClick?.();
          onRetailerClick?.();
        }
      : undefined;

  return (
    <div data-cy="stock-listing-buttons">
      {stockStatus === 'in-stock' ? (
        <BuyNow
          onClick={onBuyNow}
          showAfffiliateLogo={showAfffiliateLogo}
          selectedSize={selectedSize}
          stockListing={listing}
          buttonPosition={position}
          pagePosition={pagePosition}
          variant={
            !dynamicVariant
              ? 'forest'
              : selectedSize
                ? 'green-cta'
                : 'black-cta'
          }
        />
      ) : (
        <div
          className={
            product.allow_user_notifications && areNotificationsAllowed
              ? 'flex gap-2'
              : ''
          }
        >
          <VisitSite
            stockListing={listing}
            buttonPosition={position}
            pagePosition={pagePosition}
            onClick={onRetailerClick}
            className="min-w-0 flex-1 !px-2"
          />
          {product.allow_user_notifications && areNotificationsAllowed && (
            <div className="flex min-w-max flex-1">
              <ProductNotificationButton
                product={{
                  affiliateName: listing.affiliate.name,
                  id: product.product_id,
                  image: product.gallery[0],
                  legacyId: product.legacy_id,
                  name: product.name,
                  price: product.price,
                  stockStatus: product.stock_status
                }}
                className="h-12 w-full whitespace-nowrap"
                pagePosition="WTB"
                placement="wtb"
                buttonStyle="normal"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
